import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, uploadFile, extractApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import RichTextEditor from '@/components/marketplace/RichTextEditor';
import SEOScoreCard from '@/components/marketplace/SEOScoreCard';
import { resolveEffectiveOgImage, absoluteUrl } from '@/lib/ogImage';
import { Save, Send, Upload, Clock, Target, User as UserIcon, X, Image as ImageIcon } from 'lucide-react';

// ---- IST <-> UTC helpers (mirrors AdminCMS approach) ----
const IST_OFFSET_MIN = 5 * 60 + 30;

const utcIsoToIstInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const ist = new Date(d.getTime() + IST_OFFSET_MIN * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}T${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`;
};

const istInputToUtcIso = (local) => {
  if (!local) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const utcMs = Date.UTC(+y, +mo - 1, +d, +h, +mi) - IST_OFFSET_MIN * 60 * 1000;
  return new Date(utcMs).toISOString();
};

// Slugify helper (mirrors backend behaviour) — used to show a live
// preview of what the auto-generated slug will look like before save.
const slugifyPreview = (s) => (s || '')
  .toLowerCase()
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 120);

const emptyForm = {
  title: '', slug: '', category_id: '',
  excerpt: '', html_content: '', cover_image_url: '', cover_image_alt: '',
  og_image_url: '', og_image_alt: '',
  tags: [], tag_input: '',
  meta_title: '', meta_description: '', focus_keyword: '',
  author_name: '', author_bio: '', author_avatar_url: '',
  status: 'draft', scheduled_publish_at: '',
};

// Simple heuristic: does the focus keyword appear in title / meta / body?
const keywordChecks = (form) => {
  const kw = (form.focus_keyword || '').trim().toLowerCase();
  if (!kw) return null;
  const title = (form.title || '').toLowerCase();
  const meta = (form.meta_description || '').toLowerCase();
  const excerpt = (form.excerpt || '').toLowerCase();
  const body = (form.html_content || '').toLowerCase();
  return {
    inTitle: title.includes(kw),
    inMeta: meta.includes(kw) || excerpt.includes(kw),
    inBody: body.includes(kw),
    inSlug: (form.slug || '').toLowerCase().includes(kw.replace(/\s+/g, '-')),
  };
};

const AdminBlogEditor = ({ editMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingOg, setUploadingOg] = useState(false);

  useEffect(() => {
    api.get('/blog/categories', { params: { include_inactive: true } }).then(r => setCats(r.data));
  }, []);

  useEffect(() => {
    if (editMode && id) {
      setLoading(true);
      api.get(`/blog/posts/${id}`).then(r => {
        const d = r.data;
        setForm({
          title: d.title || '',
          slug: d.slug || '',
          category_id: d.category_id || '',
          excerpt: d.excerpt || '',
          html_content: d.html_content || '',
          cover_image_url: d.cover_image_url || '',
          cover_image_alt: d.cover_image_alt || '',
          og_image_url: d.og_image_url || '',
          og_image_alt: d.og_image_alt || '',
          tags: Array.isArray(d.tags) ? d.tags : [],
          tag_input: '',
          meta_title: d.meta_title || '',
          meta_description: d.meta_description || '',
          focus_keyword: d.focus_keyword || '',
          author_name: d.author_name || '',
          author_bio: d.author_bio || '',
          author_avatar_url: d.author_avatar_url || '',
          status: d.status || 'draft',
          scheduled_publish_at: d.scheduled_publish_at || '',
        });
      }).catch(() => toast.error('Failed to load post')).finally(() => setLoading(false));
    }
  }, [editMode, id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadImage = async (file, target, setBusy) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file, 'image', { context: 'blog_image' });
      const url = res.public_url?.startsWith('http') ? res.public_url : `${process.env.REACT_APP_BACKEND_URL || ''}${res.public_url}`;
      set(target, url);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setBusy(false); }
  };

  // Tag chip helpers
  const addTag = (raw) => {
    const s = (raw || '').trim().replace(/,+$/g, '').trim();
    if (!s) return;
    setForm(f => {
      const existingLower = new Set((f.tags || []).map(t => t.toLowerCase()));
      if (existingLower.has(s.toLowerCase())) return { ...f, tag_input: '' };
      if ((f.tags || []).length >= 15) {
        toast.warning('Max 15 tags per post');
        return { ...f, tag_input: '' };
      }
      return { ...f, tags: [...(f.tags || []), s], tag_input: '' };
    });
  };
  const removeTag = (t) => setForm(f => ({ ...f, tags: (f.tags || []).filter(x => x !== t) }));
  const onTagKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(form.tag_input); }
    else if (e.key === 'Backspace' && !form.tag_input && (form.tags || []).length) {
      // pop last tag on backspace with empty input
      setForm(f => ({ ...f, tags: f.tags.slice(0, -1) }));
    }
  };

  const kwCheck = useMemo(() => keywordChecks(form), [form]);
  // Effective OG preview for blogs: cover image (or first body image) when no
  // manual OG is set. Display-only, mirrors the public og:image resolution.
  const autoBlogOg = form.og_image_url ? null : resolveEffectiveOgImage(form, 'blog');

  const buildPayload = (statusOverride) => {
    const status = statusOverride || form.status;
    // Pending tag_input treated as a tag if user forgot to Enter
    let finalTags = form.tags || [];
    const pending = (form.tag_input || '').trim().replace(/,+$/g, '').trim();
    if (pending) {
      const lower = new Set(finalTags.map(t => t.toLowerCase()));
      if (!lower.has(pending.toLowerCase()) && finalTags.length < 15) finalTags = [...finalTags, pending];
    }
    const payload = {
      title: form.title,
      slug: form.slug,
      category_id: form.category_id || null,
      excerpt: form.excerpt,
      html_content: form.html_content,
      cover_image_url: form.cover_image_url,
      cover_image_alt: form.cover_image_alt,
      og_image_url: form.og_image_url,
      og_image_alt: form.og_image_alt,
      tags: finalTags,
      meta_title: form.meta_title,
      meta_description: form.meta_description,
      focus_keyword: form.focus_keyword,
      author_name: form.author_name,
      author_bio: form.author_bio,
      author_avatar_url: form.author_avatar_url,
      status,
      scheduled_publish_at: status === 'scheduled' ? form.scheduled_publish_at : null,
    };
    if (!payload.category_id) delete payload.category_id;
    return payload;
  };

  const save = async (statusOverride) => {
    if (!form.title || form.title.length < 5) return toast.error('Title must be at least 5 characters');
    if (!form.html_content || form.html_content.length < 20) return toast.error('Content too short');
    const status = statusOverride || form.status;
    if (status === 'scheduled') {
      if (!form.scheduled_publish_at) return toast.error('Pick a publish date & time for scheduling');
      const when = new Date(form.scheduled_publish_at).getTime();
      if (!(when > Date.now() + 30_000)) return toast.error('Scheduled time must be at least a minute in the future');
    }
    const payload = buildPayload(status);
    setLoading(true);
    try {
      if (editMode && id) {
        await api.patch(`/blog/posts/${id}`, payload);
        toast.success('Post updated');
      } else {
        await api.post('/blog/posts', payload);
        toast.success(status === 'published' ? 'Post published' : status === 'scheduled' ? 'Post scheduled' : 'Draft saved');
      }
      navigate('/admin/blog');
    } catch (e) { toast.error(extractApiError(e, 'Save failed')); }
    finally { setLoading(false); }
  };

  return (
    <div className='space-y-5 max-w-4xl'>
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <div>
          <h1 className='text-2xl font-semibold tracking-tight'>{editMode ? 'Edit post' : 'New post'}</h1>
          <p className='text-muted-foreground text-sm'>Write your article with rich text, SEO controls, and publish scheduling.</p>
        </div>
        <div className='flex gap-2'>
          <Button variant='outline' onClick={() => save('draft')} disabled={loading} data-testid='blog-save-draft'>
            <Save className='h-4 w-4 mr-1.5' /> Save draft
          </Button>
          <Button variant='outline' onClick={() => save('scheduled')} disabled={loading || !form.scheduled_publish_at} data-testid='blog-schedule'>
            <Clock className='h-4 w-4 mr-1.5' /> Schedule
          </Button>
          <Button onClick={() => save('published')} disabled={loading} data-testid='blog-publish'>
            <Send className='h-4 w-4 mr-1.5' /> Publish now
          </Button>
        </div>
      </div>

      <Card className='p-6 space-y-4' data-testid='blog-editor-basics'>
        <div className='grid md:grid-cols-2 gap-4'>
          <div className='md:col-span-2'>
            <Label>Title</Label>
            <Input
              data-testid='blog-title'
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder='e.g. How to buy used CNC machinery'
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category_id || '__none__'} onValueChange={(v) => set('category_id', v === '__none__' ? '' : v)}>
              <SelectTrigger data-testid='blog-category-select'><SelectValue placeholder='Choose category' /></SelectTrigger>
              <SelectContent>
                <SelectItem value='__none__'>(No category)</SelectItem>
                {cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Slug</Label>
            <div className='flex gap-2'>
              <Input
                value={form.slug}
                onChange={(e) => set('slug', e.target.value)}
                placeholder={form.title ? slugifyPreview(form.title) : 'my-post-slug'}
                data-testid='blog-slug-input'
              />
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={!form.title}
                onClick={() => set('slug', slugifyPreview(form.title))}
                data-testid='blog-slug-regen'
                title='Fill slug from title'
              >Auto</Button>
            </div>
            <div className='text-[11px] text-muted-foreground mt-1'>
              {form.slug
                ? <>Final URL: <code>/blog/{slugifyPreview(form.slug) || '…'}</code></>
                : (form.title
                    ? <>Leave blank to auto-generate: <code>/blog/{slugifyPreview(form.title) || '…'}</code></>
                    : 'Auto-generated from title if left empty.')}
            </div>
          </div>
          <div className='md:col-span-2'>
            <Label>Excerpt (short summary shown on listing pages)</Label>
            <Textarea rows={3} data-testid='blog-excerpt' value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} maxLength={500} />
          </div>
          <div className='md:col-span-2'>
            <Label>Cover image</Label>
            <div className='flex items-center gap-3'>
              {form.cover_image_url && (
                <img src={form.cover_image_url} alt='cover' className='h-16 w-24 object-cover rounded border' />
              )}
              <label className='inline-flex items-center gap-1.5 rounded-md border px-3 py-2 cursor-pointer text-sm hover:bg-secondary'>
                <Upload className='h-4 w-4' /> {uploadingCover ? 'Uploading…' : (form.cover_image_url ? 'Replace' : 'Upload cover')}
                <input type='file' accept='image/*' className='hidden' onChange={(e) => { uploadImage(e.target.files?.[0], 'cover_image_url', setUploadingCover); e.target.value = ''; }} data-testid='blog-cover-upload' />
              </label>
              <Input placeholder='…or paste URL' value={form.cover_image_url} onChange={(e) => set('cover_image_url', e.target.value)} className='flex-1' />
            </div>
            {form.cover_image_url && (
              <div className='mt-3'>
                <Label className='text-xs'>Cover image alt text (for SEO & accessibility)</Label>
                <Input
                  value={form.cover_image_alt}
                  onChange={(e) => set('cover_image_alt', e.target.value)}
                  maxLength={200}
                  placeholder='Describe the cover image — auto-filled from focus keyword or title on save'
                  data-testid='blog-cover-image-alt'
                />
                <div className='text-[11px] text-muted-foreground mt-1'>Used as the <code>alt</code> attribute. Leave blank to auto-populate; edit anytime.</div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className='p-6'>
        <Label className='mb-2 block'>Content</Label>
        <RichTextEditor
          value={form.html_content}
          onChange={(html) => set('html_content', html)}
        />
      </Card>

      <Card className='p-6 space-y-4' data-testid='blog-seo-card'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Target className='h-4 w-4 text-primary' />
            <div className='font-semibold'>SEO &amp; Tags</div>
          </div>
          {kwCheck && (
            <div className='flex flex-wrap gap-1' data-testid='blog-keyword-status'>
              <ChipStatus ok={kwCheck.inTitle} label='Title' />
              <ChipStatus ok={kwCheck.inMeta} label='Meta / excerpt' />
              <ChipStatus ok={kwCheck.inBody} label='Body' />
              <ChipStatus ok={kwCheck.inSlug} label='Slug' />
            </div>
          )}
        </div>

        <SEOScoreCard values={form} contentType='blog' />

        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>Focus keyword</Label>
            <Input
              data-testid='blog-focus-keyword'
              value={form.focus_keyword}
              onChange={(e) => set('focus_keyword', e.target.value)}
              maxLength={120}
              placeholder='e.g. used CNC lathe'
            />
            <div className='text-[11px] text-muted-foreground mt-1'>
              The primary phrase this post should rank for.
            </div>
          </div>
          <div>
            <Label>Meta title (auto from title if empty)</Label>
            <Input value={form.meta_title} onChange={(e) => set('meta_title', e.target.value)} maxLength={70} data-testid='blog-meta-title' />
          </div>
          <div className='md:col-span-2'>
            <Label>Meta description</Label>
            <Textarea rows={2} maxLength={170} value={form.meta_description} onChange={(e) => set('meta_description', e.target.value)} data-testid='blog-meta-desc' />
            <div className='text-[11px] text-muted-foreground text-right mt-0.5'>{(form.meta_description || '').length}/170</div>
          </div>
          <div className='md:col-span-2'>
            <Label>Open Graph image (for social sharing)</Label>
            <div className='flex items-center gap-3'>
              {form.og_image_url ? (
                <img src={form.og_image_url} alt='og' className='h-16 w-28 object-cover rounded border' data-testid='blog-og-image-manual-preview' />
              ) : autoBlogOg ? (
                <img src={absoluteUrl(autoBlogOg)} alt='auto og preview' className='h-16 w-28 object-cover rounded border opacity-80' data-testid='blog-og-image-auto-preview' />
              ) : (
                <div className='h-16 w-28 rounded border bg-muted flex items-center justify-center text-muted-foreground'>
                  <ImageIcon className='h-5 w-5' />
                </div>
              )}
              <label className='inline-flex items-center gap-1.5 rounded-md border px-3 py-2 cursor-pointer text-sm hover:bg-secondary'>
                <Upload className='h-4 w-4' /> {uploadingOg ? 'Uploading…' : (form.og_image_url ? 'Replace' : 'Upload')}
                <input type='file' accept='image/*' className='hidden' onChange={(e) => { uploadImage(e.target.files?.[0], 'og_image_url', setUploadingOg); e.target.value = ''; }} data-testid='blog-og-upload' />
              </label>
              <Input placeholder='…or paste URL (falls back to cover image)' value={form.og_image_url} onChange={(e) => set('og_image_url', e.target.value)} className='flex-1' data-testid='blog-og-image-url-input' />
            </div>
            {!form.og_image_url && (
              <div className='text-[11px] text-muted-foreground mt-1' data-testid='blog-og-image-effective-note'>
                {autoBlogOg
                  ? 'No manual OG image — social previews will automatically use the cover image. Upload or paste a URL to override it.'
                  : 'No OG image available yet. Social previews will fall back to the site default.'}
              </div>
            )}
            <div className='mt-3'>
              <Label className='text-xs'>OG image alt text</Label>
              <Input
                value={form.og_image_alt}
                onChange={(e) => set('og_image_alt', e.target.value)}
                maxLength={200}
                placeholder='Describe the social share image — auto-filled from focus keyword or title on save'
                data-testid='blog-og-image-alt'
              />
              <div className='text-[11px] text-muted-foreground mt-1'>Applied to the OG image for social previews. Leave blank to auto-populate.</div>
            </div>
          </div>
          <div className='md:col-span-2'>
            <Label>Tags</Label>
            <div className='flex flex-wrap gap-1.5 rounded-md border p-2 bg-background focus-within:ring-2 focus-within:ring-ring/50'
                 data-testid='blog-tags-input-wrapper'
                 onClick={(e) => e.currentTarget.querySelector('input')?.focus()}>
              {(form.tags || []).map(t => (
                <span key={t} className='inline-flex items-center gap-1 rounded-full bg-secondary text-secondary-foreground text-xs px-2 py-1' data-testid={`blog-tag-chip-${t}`}>
                  {t}
                  <button type='button' onClick={() => removeTag(t)} className='hover:text-destructive' aria-label={`Remove ${t}`}>
                    <X className='h-3 w-3' />
                  </button>
                </span>
              ))}
              <input
                type='text'
                data-testid='blog-tag-input'
                className='flex-1 min-w-[140px] bg-transparent outline-none text-sm py-1'
                value={form.tag_input}
                placeholder={(form.tags || []).length >= 15 ? 'Max 15 tags' : 'Type a tag, press Enter or comma'}
                onChange={(e) => set('tag_input', e.target.value)}
                onKeyDown={onTagKey}
                onBlur={() => addTag(form.tag_input)}
                disabled={(form.tags || []).length >= 15}
              />
            </div>
            <div className='text-[11px] text-muted-foreground mt-1'>Press Enter or comma to add. {(form.tags || []).length}/15 tags.</div>
          </div>
        </div>
      </Card>

      <Card className='p-6 space-y-4' data-testid='blog-author-card'>
        <div className='flex items-center gap-2'>
          <UserIcon className='h-4 w-4 text-primary' />
          <div className='font-semibold'>Author</div>
        </div>
        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>Author name (optional — shown on the article)</Label>
            <Input value={form.author_name} onChange={(e) => set('author_name', e.target.value)} placeholder='e.g. IndusFact Editorial' maxLength={100} data-testid='blog-author-name' />
          </div>
          <div>
            <Label>Author avatar URL</Label>
            <Input value={form.author_avatar_url} onChange={(e) => set('author_avatar_url', e.target.value)} placeholder='https://…' data-testid='blog-author-avatar' />
          </div>
          <div className='md:col-span-2'>
            <Label>Author bio</Label>
            <Textarea rows={2} maxLength={500} value={form.author_bio} onChange={(e) => set('author_bio', e.target.value)} placeholder='A short one-line description of the author.' data-testid='blog-author-bio' />
          </div>
        </div>
      </Card>

      <Card className='p-6 space-y-4' data-testid='blog-publish-card'>
        <div className='flex items-center gap-2'>
          <Clock className='h-4 w-4 text-primary' />
          <div className='font-semibold'>Publish schedule</div>
        </div>
        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>Current status</Label>
            <div className='mt-1'>
              <StatusBadge status={form.status} scheduled_at={form.scheduled_publish_at} />
            </div>
          </div>
          <div>
            <Label>Schedule publish at (IST)</Label>
            <Input
              type='datetime-local'
              value={utcIsoToIstInput(form.scheduled_publish_at)}
              onChange={(e) => set('scheduled_publish_at', istInputToUtcIso(e.target.value))}
              data-testid='blog-schedule-datetime'
            />
            <div className='text-[11px] text-muted-foreground mt-1'>
              India time (UTC+5:30). Click <strong>Schedule</strong> to save with this publish time — the post will auto-publish within 5 minutes of the scheduled time.
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

const ChipStatus = ({ ok, label }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${ok ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700' : 'bg-muted text-muted-foreground border-border'}`}>
    {ok ? '✓' : '•'} {label}
  </span>
);

const StatusBadge = ({ status, scheduled_at }) => {
  if (status === 'published') return <Badge className='bg-emerald-500/10 text-emerald-700 border-emerald-500/30' variant='outline'>Published</Badge>;
  if (status === 'scheduled') {
    const when = scheduled_at ? new Date(scheduled_at) : null;
    const label = when ? `Scheduled · ${when.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Scheduled';
    return <Badge className='bg-amber-500/10 text-amber-700 border-amber-500/30' variant='outline'>{label}</Badge>;
  }
  return <Badge variant='outline'>Draft</Badge>;
};

export default AdminBlogEditor;
